import { FC } from 'react'
import { PixelText, InfoButton } from '@/components/retro'
import { useGameStore } from '@/store'
import { formatGameTime, formatTimeRemaining, getDegenTimeDescription } from '@/game/clock'

export const GameStatusPanel: FC = () => {
  const { session } = useGameStore()

  if (!session) return null

  const { goal, player, gameTime, startingAmount, difficulty } = session
  const { totalValue } = player.portfolio

  // Calculate progress
  const progressPercent = (totalValue / goal.targetAmount) * 100
  const deadlineDays = Math.ceil(
    (goal.deadline.getTime() - session.startedAt.getTime()) / (1000 * 60 * 60 * 24)
  )

  // Format values
  const formatCurrency = (value: number): string => {
    if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`
    if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`
    return `$${value.toFixed(0)}`
  }

  const difficultyColors = {
    paper_hands: 'text-game-profit',
    diamond_hands: 'text-game-gold',
    gigabrain: 'text-game-loss',
  }

  const difficultyLabels = {
    paper_hands: 'PAPER HANDS',
    diamond_hands: 'DIAMOND HANDS',
    gigabrain: 'GIGABRAIN',
  }

  const difficultyDescriptions = {
    paper_hands: 'Easy mode: More time, lower target. Perfect for learning the ropes.',
    diamond_hands: 'Normal mode: Balanced challenge for experienced degens.',
    gigabrain: 'Hard mode: Less time, higher target. Only for true believers.',
  }

  return (
    <div className="p-3 space-y-3">
      {/* Time & Difficulty */}
      <div className="flex justify-between items-center pb-2 border-b border-game-border">
        <div>
          <PixelText size="sm" color="accent">
            {formatGameTime(gameTime)}
          </PixelText>
          <p className="font-retro text-sm text-game-muted mt-0.5">
            {getDegenTimeDescription(gameTime)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`font-pixel text-sm ${difficultyColors[difficulty]}`}>
            {difficultyLabels[difficulty]}
          </span>
          <InfoButton
            title="Difficulty"
            content={difficultyDescriptions[difficulty]}
            position="left"
          />
        </div>
      </div>

      {/* Goal Progress - Enhanced */}
      <div className={`p-3 border-2 ${progressPercent >= 100 ? 'border-game-profit bg-game-profit/10' : progressPercent >= 75 ? 'border-game-gold bg-game-gold/10' : 'border-game-border'}`}>
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-1">
            <span className="font-pixel text-sm text-game-accent">GOAL PROGRESS</span>
            <InfoButton
              title="Goal Progress"
              content="Fill the bar to reach your target amount. The gold marker shows your goal. Reach 100% to win!"
              position="right"
            />
          </div>
          <span className={`font-pixel text-lg ${
            progressPercent >= 100 ? 'text-game-profit animate-pulse' :
            progressPercent >= 75 ? 'text-game-gold' :
            'text-game-text'
          }`}>
            {progressPercent >= 100 ? 'GOAL MET!' : `${progressPercent.toFixed(0)}%`}
          </span>
        </div>

        {/* Enhanced Progress Bar */}
        <div className="relative w-full h-8 bg-black border-2 border-game-border overflow-hidden">
          {/* Milestone markers */}
          <div className="absolute inset-0 flex">
            <div className="flex-1 border-r border-game-border/30" /> {/* 25% */}
            <div className="flex-1 border-r border-game-border/30" /> {/* 50% */}
            <div className="flex-1 border-r border-game-border/30" /> {/* 75% */}
            <div className="flex-1" /> {/* 100% */}
          </div>

          {/* Progress fill */}
          <div
            className={`absolute left-0 top-0 h-full transition-all duration-500 ${
              progressPercent >= 100 ? 'bg-game-profit' :
              progressPercent >= 75 ? 'bg-game-gold' :
              progressPercent >= 50 ? 'bg-game-accent' :
              'bg-game-accent/70'
            }`}
            style={{ width: `${Math.min(progressPercent, 100)}%` }}
          />

          {/* Milestone markers on top */}
          <div className="absolute inset-0 flex pointer-events-none">
            {[25, 50, 75].map(milestone => (
              <div key={milestone} className="flex-1 flex justify-end">
                <div className={`w-0.5 h-full ${progressPercent >= milestone ? 'bg-white/50' : 'bg-game-border/50'}`} />
              </div>
            ))}
            <div className="flex-1" />
          </div>

          {/* Goal marker (100%) */}
          <div className="absolute right-0 top-0 h-full w-1 bg-game-gold z-10" />

          {/* Value labels */}
          <div className="absolute inset-0 flex items-center justify-between px-2 z-20">
            <span className="font-mono text-sm text-white drop-shadow-[0_1px_1px_rgba(0,0,0,1)] font-bold">
              {formatCurrency(totalValue)}
            </span>
            <span className="font-mono text-sm text-game-gold drop-shadow-[0_1px_1px_rgba(0,0,0,1)]">
              {formatCurrency(goal.targetAmount)}
            </span>
          </div>
        </div>

        {/* Milestone labels */}
        <div className="flex justify-between text-[10px] font-mono text-game-muted mt-1 px-1">
          <span>0%</span>
          <span>25%</span>
          <span>50%</span>
          <span>75%</span>
          <span className="text-game-gold">GOAL</span>
        </div>
      </div>

      {/* Goal Description */}
      <div className="p-2 bg-game-panel/50 border border-game-border">
        <p className="font-retro text-sm text-game-muted text-center italic">
          "{goal.description}"
        </p>
      </div>

      {/* Time Remaining */}
      <div className="flex justify-between items-center p-2 bg-game-panel/50 border border-game-border">
        <div className="flex items-center gap-1">
          <span className="font-retro text-sm text-game-muted">Time Remaining</span>
          <InfoButton
            title="Deadline"
            content="Time left to reach your goal. If you run out of time, the game ends. Use WAIT/SLEEP to advance time strategically."
            position="right"
          />
        </div>
        <span className={`font-pixel text-sm ${
          gameTime.gameDay > deadlineDays - 3 ? 'text-game-loss animate-pulse' : 'text-game-gold'
        }`}>
          {formatTimeRemaining(gameTime, deadlineDays)}
        </span>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-2">
        <div className="p-2 bg-game-panel/50 border border-game-border text-center">
          <div className="font-retro text-sm text-game-muted">Started With</div>
          <div className="font-mono text-base text-game-text">{formatCurrency(startingAmount)}</div>
        </div>
        <div className="p-2 bg-game-panel/50 border border-game-border text-center">
          <div className="font-retro text-sm text-game-muted">Need to Gain</div>
          <div className={`font-mono text-base ${
            totalValue >= goal.targetAmount ? 'text-game-profit' : 'text-game-gold'
          }`}>
            {totalValue >= goal.targetAmount
              ? 'GOAL MET!'
              : formatCurrency(goal.targetAmount - totalValue)
            }
          </div>
        </div>
      </div>

      {/* Multiplier */}
      <div className="flex items-center justify-center gap-2 p-2 bg-game-panel/50 border border-game-border">
        <span className="font-retro text-sm text-game-muted">Current Multiplier:</span>
        <span className={`font-pixel text-base ${
          totalValue >= startingAmount ? 'text-game-profit' : 'text-game-loss'
        }`}>
          {(totalValue / startingAmount).toFixed(2)}x
        </span>
        <InfoButton
          title="Multiplier"
          content="How much your portfolio has grown (or shrunk) from your starting amount. Green = gains, Red = losses."
          position="left"
        />
      </div>
    </div>
  )
}
